import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Text,
  Banner,
  TextField,
  Select,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useRestorableState from "hooks/useRestorableState";
import { ViewIcon, CircleDownIcon } from "@shopify/polaris-icons";
import { XCircle } from "lucide-react";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { useAppSelector, useAppDispatch } from "hooks";
import { RootState } from "configureStore";
import DataTable from "components/datatable/Datatable";
import Loading from "components/loading/Loading";
import { API_URL } from "constants.js";
import PrettyDate from "components/format/PrettyDate";
import { fetchUnsignedAssignedFiles } from "services/files-category.service";
import { isSignatureLockedFile } from "utils/signatureUtils";
import useIsPhone from "features/files/use-is-phone";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

type CompanyFilterOption = { id: string; name: string };

type ListFilters = {
  textSearch: string;
  companyId: string;
  signType: string;
};

type DokobitSessionState = "active" | "expiring" | "expired";

const DOKOBIT_SESSION_STATES: DokobitSessionState[] = [
  "active",
  "expiring",
  "expired",
];

function toNullableInteger(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function toDokobitSessionState(value: unknown): DokobitSessionState | null {
  return DOKOBIT_SESSION_STATES.includes(value as DokobitSessionState)
    ? (value as DokobitSessionState)
    : null;
}

// Plain-string date for title/tooltip attributes (PrettyDate renders a node).
function formatDateForTitle(iso: string | null, showTime = false): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/Vilnius",
  };
  if (showTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }
  const parts = new Intl.DateTimeFormat("lt-LT", options).formatToParts(date);
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  const day = `${part("year")}-${part("month")}-${part("day")}`;
  return showTime ? `${day} ${part("hour")}:${part("minute")}` : day;
}

function mapFileToTableRow(file: any, t: (key: string, opts?: object) => string) {
  const fileCompanyId =
    file.company?.id ||
    file.companyId ||
    (typeof file.company === "string" ? file.company : null);
  const categoryId =
    file.filesToCategories?.[0]?.id ||
    file.category?.id ||
    file.fileCategory;

  const fileId = file.id;
  const reminderIntervalDays = file?.reminderIntervalDays ?? null;
  const reminderEnabled = file?.reminderEnabled === true;
  const reminderScheduleText =
    reminderEnabled && reminderIntervalDays
      ? `${reminderIntervalDays} ${
          reminderIntervalDays === 1 ? t("day") : t("days")
        }`
      : t("reminders_disabled_short");

  const nextReminderAt: string | null = file?.nextReminderAt || null;
  const sentRemindersCount: number = file?.sentRemindersCount ?? 0;
  const isOverdue =
    reminderEnabled &&
    !!nextReminderAt &&
    new Date(nextReminderAt).getTime() <= Date.now();

  // Session fields come from the API contract; an older backend omits them,
  // which must read as "no Dokobit session info" (no pill, no red row).
  const dokobitSessionState = toDokobitSessionState(file?.dokobitSessionState);
  const dokobitSessionDaysLeft = dokobitSessionState
    ? toNullableInteger(file?.dokobitSessionDaysLeft)
    : null;
  const dokobitSessionExpiresAt: string | null = dokobitSessionState
    ? file?.dokobitSessionExpiresAt || null
    : null;

  return {
    rawFile: file,
    id: file.id,
    company: file.company?.legalName || file.company?.name || "-",
    companyId: fileCompanyId,
    file: file.name || file.title || "-",
    fileId,
    title: file?.title,
    categoryId,
    reminderEnabled,
    reminderIntervalDays,
    nextReminderAt: file?.nextReminderAt || null,
    sentRemindersCount,
    isOverdue,
    reminderScheduleText,
    downloadUrl: fileCompanyId
      ? `${API_URL}/${fileCompanyId}/files/download/${file.id}`
      : "",
    customers:
      file.customers?.map((cust: any) => cust.name).join(", ") ||
      file.customer?.name ||
      "-",
    signatureRequestedDate: file.signatureRequestedDate || null,
    daysWaiting: toNullableInteger(file?.daysWaiting),
    dokobitSessionState,
    dokobitSessionDaysLeft,
    dokobitSessionExpiresAt,
    awaitingSignersCount: Number(file?.awaitingSignersCount ?? 0),
    collectedSignersCount: Number(file?.collectedSignersCount ?? 0),
  };
}

function isDokobitSessionAtRisk(state: unknown): boolean {
  return state === "expiring" || state === "expired";
}

interface UnsignedAssignedDocumentsProps {
  onRefreshingChange?: (refreshing: boolean) => void;
}

const UnsignedAssignedDocuments: React.FC<UnsignedAssignedDocumentsProps> = ({
  onRefreshingChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // On a phone the list has no scroll box of its own: the page scrolls, so "load more" watches the screen.
  const isPhone = useIsPhone();

  const refreshNonce = useAppSelector(
    (state: RootState) => state.unsignedFilesReducer.refreshNonce
  );

  const [files, setFiles] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Remembered so Back from a document returns to the same filtered list.
  const [textSearch, setTextSearch] = useRestorableState(
    "unsigned-documents-search",
    ""
  );
  const [appliedTextSearch, setAppliedTextSearch] = useRestorableState(
    "unsigned-documents-applied-search",
    ""
  );
  const [companyFilter, setCompanyFilter] = useRestorableState(
    "unsigned-documents-company",
    ""
  );
  const [signTypeFilter, setSignTypeFilter] = useRestorableState(
    "unsigned-documents-sign-type",
    ""
  );
  const [companyOptions, setCompanyOptions] = useState<CompanyFilterOption[]>([]);
  const [signTypeOptions, setSignTypeOptions] = useState<string[]>([]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const listRequestRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const filtersRef = useRef<ListFilters>({
    textSearch: "",
    companyId: "",
    signType: "",
  });

  const appliedFilters: ListFilters = {
    textSearch: appliedTextSearch,
    companyId: companyFilter,
    signType: signTypeFilter,
  };
  const filtersKey = `${appliedTextSearch}|${companyFilter}|${signTypeFilter}`;

  const hasActiveFilters = Boolean(
    appliedTextSearch.trim() || companyFilter || signTypeFilter
  );
  const isSearchPending =
    textSearch.trim().length > 0 &&
    textSearch.trim().length < MIN_SEARCH_LENGTH &&
    textSearch.trim() !== appliedTextSearch;

  hasMoreRef.current = hasMore;
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;
  filtersRef.current = appliedFilters;

  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  // Debounced search — waits 400ms after typing; API only when cleared or 2+ chars.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const trimmed = textSearch.trim();
      if (trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH) {
        setAppliedTextSearch(trimmed);
      } else {
        setAppliedTextSearch("");
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [textSearch, setAppliedTextSearch]);

  const loadPage = useCallback(async (targetPage: number, append: boolean) => {
    const requestId = ++listRequestRef.current;
    const filters = filtersRef.current;

    try {
      if (append) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        loadingRef.current = true;
        setLoading(true);
        setLoadError(false);
      }

      const data = await fetchUnsignedAssignedFiles({
        page: targetPage,
        pageSize: PAGE_SIZE,
        textSearch: filters.textSearch,
        companyId: filters.companyId,
        signType: filters.signType,
      });

      if (requestId !== listRequestRef.current) return;

      if (!data || Array.isArray(data)) {
        const entries = Array.isArray(data) ? data : [];
        setFiles((current) => (append ? [...current, ...entries] : entries));
        setHasMore(false);
        hasMoreRef.current = false;
        pageRef.current = append ? targetPage : 1;
        if (!append) {
          setHasLoadedOnce(true);
        }
        return;
      }

      const entries = data.entries || [];
      if (data.filterOptions?.companies) {
        setCompanyOptions(data.filterOptions.companies);
      }
      if (data.filterOptions?.signTypes) {
        setSignTypeOptions(data.filterOptions.signTypes);
      }

      const nextHasMore = Boolean(data.pagination?.hasMore);
      setFiles((current) => (append ? [...current, ...entries] : entries));
      setHasMore(nextHasMore);
      hasMoreRef.current = nextHasMore;
      pageRef.current = targetPage;
      if (!append) {
        setHasLoadedOnce(true);
      }
    } catch (err) {
      if (requestId !== listRequestRef.current) return;
      console.error("Failed to load unsigned assigned files", err);
      setLoadError(true);
      if (!append) {
        setFiles([]);
        setHasMore(false);
        hasMoreRef.current = false;
        pageRef.current = 1;
      }
    } finally {
      if (requestId === listRequestRef.current) {
        loadingRef.current = false;
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const tryLoadMore = useCallback(() => {
    if (
      !hasMoreRef.current ||
      loadingRef.current ||
      loadingMoreRef.current
    ) {
      return;
    }
    loadPage(pageRef.current + 1, true);
  }, [loadPage]);

  const isRefreshing = loading && !loadingMore && hasLoadedOnce;

  useEffect(() => {
    onRefreshingChange?.(isRefreshing);
  }, [isRefreshing, onRefreshingChange]);

  // Refetch first page when filters or refresh nonce change (keep current rows visible).
  useEffect(() => {
    pageRef.current = 1;
    hasMoreRef.current = false;
    setHasMore(false);
    loadPage(1, false);
  }, [filtersKey, refreshNonce, loadPage]);

  // Re-check sentinel after list updates (short pages / filter changes).
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const frameId = window.requestAnimationFrame(() => {
      const root = scrollRoot;
      const sentinel = sentinelRef.current;
      if (!root || !sentinel || !hasMoreRef.current) return;

      const visibleBottom = isPhone
        ? window.innerHeight
        : root.getBoundingClientRect().bottom;
      const sentinelRect = sentinel.getBoundingClientRect();
      if (sentinelRect.top <= visibleBottom + 200) {
        tryLoadMore();
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [scrollRoot, isPhone, files.length, hasMore, loading, loadingMore, filtersKey, tryLoadMore]);

  // Infinite scroll observer — uses refs so callbacks never go stale.
  useEffect(() => {
    const root = scrollRoot;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          tryLoadMore();
        }
      },
      { root: isPhone ? null : root, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollRoot, isPhone, tryLoadMore, files.length, filtersKey]);

  const handlePdfView = (fileId: string, fileCompanyId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(
      setModalxComponentProps({
        id: fileId,
        companyId: fileCompanyId,
      })
    );
  };

  const handleRowClick = (companyId: string, _categoryId: string, fileId: string) => {
    if (companyId && fileId) {
      navigate(`/app/${companyId}/documents/view/${fileId}`);
    }
  };

  const handleOpenReminderModal = (fileId: string, companyId: string) => {
    dispatch(setModalxComponent("ReminderSettingsModal"));
    dispatch(setModalxStyle("large"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
      })
    );
  };

  const handleReminder = (fileId: string, companyId: string) => {
    if (!fileId || !companyId) return;
    handleOpenReminderModal(fileId, companyId);
  };

  // "Atšaukti pasirašymą": drop every pending request so the document leaves
  // this list. The confirm modal refreshes the badge + list on success.
  const handleCancelSigning = (row: any) => {
    if (!row?.fileId || !row?.companyId) return;
    dispatch(setModalxComponent("FileSigningCancelConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: row.fileId,
        companyId: row.companyId,
        fileName: row.file,
        awaitingCount: row.awaitingSignersCount,
        collectedCount: row.collectedSignersCount,
      })
    );
  };

  const filesCollection = files
    .filter((file) => file && file.id)
    .map((file) => {
      const row = mapFileToTableRow(file, t);
      // Owner's signing rule: a signature/signature request on a locked
      // document may never be removed — the backend would 400 with
      // template_signatures_locked anyway, so drop the bulk "cancel signing"
      // action for these rows before it gets that far. Some template-generated
      // documents (e.g. work-security roles) are deliberately not locked —
      // isSignatureLockedFile reads the backend's own signaturesLocked flag.
      const isRowTemplateGenerated = isSignatureLockedFile(file);
      const {
        fileId,
        fileCompanyId,
        reminderEnabled,
        reminderScheduleText,
        isOverdue,
        nextReminderAt,
        sentRemindersCount,
      } = {
        fileId: row.fileId,
        fileCompanyId: row.companyId,
        reminderEnabled: row.reminderEnabled,
        reminderScheduleText: row.reminderScheduleText,
        isOverdue: row.isOverdue,
        nextReminderAt: row.nextReminderAt,
        sentRemindersCount: row.sentRemindersCount,
      };

      const reminderMetaNode = (
        <div className="inline-flex flex-col items-center text-center">
          <div className="flex flex-col items-center gap-0.5 text-xs leading-4">
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                className={`underline decoration-dotted underline-offset-2 hover:text-[#B54708] whitespace-nowrap max-md:inline-flex max-md:min-h-11 max-md:items-center ${
                  reminderEnabled
                    ? "font-semibold text-[#3F3227]"
                    : "text-gray-500"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReminder(fileId, fileCompanyId);
                }}
                aria-label={t("reminders")}
              >
                {reminderScheduleText}
              </button>
              {reminderEnabled && isOverdue && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FEF3F2] text-[#B42318] text-[11px]">
                  {t("reminder_overdue")}
                </span>
              )}
            </div>
            {reminderEnabled && (
              <>
                <div className="text-gray-600">
                  {t("next_reminder_short")}{" "}
                  <span className="whitespace-nowrap">
                    {nextReminderAt ? (
                      <PrettyDate dateString={nextReminderAt} />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="text-gray-600">
                  {t("sent_short")}:{" "}
                  <span className="font-medium text-[#3F3227]">
                    {sentRemindersCount}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      );

      const sessionAtRisk = isDokobitSessionAtRisk(row.dokobitSessionState);
      const sessionPillText =
        row.dokobitSessionState === "expired"
          ? t("dokobit_session_expired")
          : t("dokobit_session_expiring", {
              count: row.dokobitSessionDaysLeft ?? 0,
            });
      const sessionExpiresAtText = formatDateForTitle(row.dokobitSessionExpiresAt);
      const waitingMetaNode = (
        <div className="inline-flex flex-col items-center gap-1 text-center">
          <span
            className="whitespace-nowrap text-xs text-[#3F3227]"
            title={formatDateForTitle(row.signatureRequestedDate, true) || undefined}
          >
            {row.daysWaiting === null ? (
              "-"
            ) : (
              <>
                <span className="font-semibold">{row.daysWaiting}</span>{" "}
                {t("days_short")}
              </>
            )}
          </span>
          {sessionAtRisk && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FEF3F2] text-[#B42318] text-[11px] whitespace-nowrap"
              title={
                sessionExpiresAtText
                  ? t("dokobit_session_expires_at", { date: sessionExpiresAtText })
                  : undefined
              }
            >
              {sessionPillText}
            </span>
          )}
        </div>
      );

      const totalSigners = row.collectedSignersCount + row.awaitingSignersCount;
      const signersMetaNode = (
        <span className="whitespace-nowrap text-xs text-gray-600">
          <span className="font-semibold text-[#3F3227]">
            {row.collectedSignersCount}
          </span>
          {" / "}
          {totalSigners}
        </span>
      );

      // Rendered through a "clickableColumn" (the cell itself handles the
      // click via the column's onClick), so this is a plain label — a nested
      // <button> would be invalid markup, and a bare node in a typeless
      // column gets stringified by DataTable.
      const cancelActionNode = isRowTemplateGenerated ? null : (
        <span
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 max-md:min-h-11 max-md:px-3 text-xs text-gray-700 hover:border-[#B42318] hover:text-[#B42318] whitespace-nowrap"
          title={t("cancel_signing_hint")}
        >
          <XCircle className="w-3.5 h-3.5" aria-hidden />
          {t("cancel_signing")}
        </span>
      );

      return {
        ...row,
        isTemplateGenerated: isRowTemplateGenerated,
        waitingMeta: waitingMetaNode,
        reminderMeta: reminderMetaNode,
        signersMeta: signersMetaNode,
        cancelAction: cancelActionNode,
      };
    });

  if (!hasLoadedOnce && loading && files.length === 0) {
    return <Loading />;
  }

  return (
    <div className={`space-y-4${isRefreshing ? " opacity-80 pointer-events-none" : ""}`}>
      {loadError && (
        <Banner tone="critical">
          {t("not_fetch", { defaultValue: "Failed to load data" })}
        </Banner>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-2 items-end">
          <div className="md:col-span-2">
            <TextField
              label={t("search", { defaultValue: "Search" })}
              value={textSearch}
              onChange={setTextSearch}
              placeholder={t("unsigned_files_search_placeholder", {
                defaultValue: "Search by file, company, or customer...",
              })}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTextSearch("")}
              helpText={
                isSearchPending
                  ? t("unsigned_files_search_min_chars", {
                      defaultValue: "Type at least 2 characters to search",
                    })
                  : undefined
              }
            />
          </div>
          <div>
            <Select
              label={t("company", { defaultValue: "Company" })}
              options={[
                { label: t("all_companies", { defaultValue: "All Companies" }), value: "" },
                ...companyOptions.map((company) => ({
                  label: company.name,
                  value: company.id,
                })),
              ]}
              value={companyFilter}
              onChange={setCompanyFilter}
            />
          </div>
          <div>
            <Select
              label={t("sign_type", { defaultValue: "Sign Type" })}
              options={[
                { label: t("all_sign_types", { defaultValue: "All Sign Types" }), value: "" },
                ...signTypeOptions.map((signType) => ({
                  label: signType,
                  value: signType,
                })),
              ]}
              value={signTypeFilter}
              onChange={setSignTypeFilter}
            />
          </div>
        </div>
      </Card>

      {filesCollection.length > 0 ? (
        <Card>
          <div
            ref={scrollContainerRef}
            data-scroll-restore="unsigned-documents-list"
            className="md:max-h-[calc(100vh-280px)] md:overflow-y-auto"
          >
            <DataTable
              collection={filesCollection}
              rowClassName={(item: any) =>
                isDokobitSessionAtRisk(item?.dokobitSessionState)
                  ? "bg-[#FEF3F2]"
                  : ""
              }
              columns={[
                { key: "#" },
                {
                  key: "company",
                  name: t("company"),
                  translationRequired: true,
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "file",
                  name: t("file"),
                  translationRequired: true,
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "customers",
                  name: t("customers"),
                  translationRequired: true,
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "signatureRequestedDate",
                  name: t("signature_request_date"),
                  type: "date",
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "waitingMeta",
                  name: t("waiting_days"),
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "signersMeta",
                  name: t("signers_progress"),
                  onClick: (item: any) =>
                    handleRowClick(item.companyId, item.categoryId, item.fileId),
                },
                {
                  key: "reminderMeta",
                  name: t("reminders"),
                  // Not a clickable column: the node holds its own button, and a column click would wrap it in a second one.
                  type: "custom",
                  render: (item: any) => item.reminderMeta,
                },
                {
                  key: "view",
                  name: t("view"),
                  onClick: (item: any) =>
                    handlePdfView(item.fileId, item.companyId),
                  type: "specificAction",
                  Icon: ViewIcon,
                },
                {
                  key: "downloadUrl",
                  name: t("download"),
                  onClick: (item: any) => {
                    if (item.downloadUrl) {
                      window.open(item.downloadUrl, "_blank");
                    }
                  },
                  url: "downloadUrl",
                  type: "downloadurl",
                  Icon: CircleDownIcon,
                },
                {
                  key: "cancelAction",
                  name: "",
                  type: "clickableColumn",
                  // Template-generated rows render no pill (cancelAction is
                  // null) and are disabled defensively — the backend would
                  // 400 with template_signatures_locked anyway.
                  isDisabled: (item: any) => item.isTemplateGenerated,
                  onClick: (item: any) => handleCancelSigning(item),
                },
              ]}
            />
            <div ref={sentinelRef} className="h-4" aria-hidden />
            {(loadingMore || (loading && files.length > 0)) && (
              <div className="flex justify-center py-4">
                <Spinner size="small" accessibilityLabel={t("loading")} />
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Text as="p">
            {isRefreshing
              ? t("loading", { defaultValue: "Loading..." })
              : hasActiveFilters
                ? t("no_unsigned_documents_match_filters", {
                    defaultValue: "No unsigned documents match your filters.",
                  })
                : t("no_unsigned_documents")}
          </Text>
        </Card>
      )}
    </div>
  );
};

export default UnsignedAssignedDocuments;
